import { useCallback, useRef, useState } from 'react';

import {
  ActivityBuilderDrawerActions,
  ActivityBuilderDrawerContent,
  ActivityBuilderDrawerHeader,
  ActivityBuilderDrawerWrapper,
} from 'approot/activity-builder/activity-builder-drawer';
import { NegativeButton, PrimaryButton } from 'approot/shared/styles/buttons';
import { ActivityBuilderDrawerForm } from 'approot/activity-builder/activity-builder-drawer.style';
import { useVocabularySettingContext } from './vocabulary-setting.context';
import {
  LANGUAGE_CODE_EN_US,
  LANGUAGE_CODE_ES_US,
  LanguageCodes,
} from 'global/constants';
import { useLoggingMutation } from 'lib/apollo/apollo-hooks';
import { DELETE_FROM_DICTIONARY } from 'approot/dictionary/dictionary.graphql';
import { useSnackbar } from 'approot/shared/snackbar-notification/snackbar-notification.hooks';
import { VocabularySettingForm } from './vocabulary-setting-form';
import {
  initialiseDefinitions,
  initialiseExamples,
  initialiseWordsTranslation,
} from './vocabulary-setting.utils';
import {
  assignTranslations,
  collectSentences,
  getFieldErrors,
  hasSavedTranslations,
  hasTranslations,
  validateForm,
} from './vocabulary-setting-form.utils';
import {
  DictionaryDefinition,
  FormDefinition,
  FormExample,
  FormWordsTranslation,
} from './vocabulary-setting.constants';
// import { DropdownKebab } from 'approot/shared/dropdown/dropdown-kebab';
// import { DropdownItem } from 'approot/shared/dropdown/dropdown-item.style';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import {
  DeleteWordFromDictionaryConfirm,
  RemoveTranslationFromDictionaryConfirm,
} from './vocabulary-setting-shared';
import type {
  DeleteFromDictionaryMutation,
  DeleteFromDictionaryMutationVariables,
} from '../../../../dictionary/dictionary.graphql.generated';
import {
  useRemoveDefinition,
  useSaveToDictionaryBook,
  useTranslateTo,
  useRemoveFromDictionaryBook,
} from './vocabulary-setting.hooks';

const VocabularySettingEditDefinition = ({
  componentId,
  sectionId,
  definition,
  allDefinitions,
  goBack,
  goTo,
}: {
  componentId: string;
  sectionId: string;
  definition: DictionaryDefinition;
  allDefinitions: DictionaryDefinition[];
  goBack: () => void;
  goTo: (id: number) => void;
}) => {
  const { setWord, setFormErrors, setCurrentLanguage, currentLanguage } =
    useVocabularySettingContext();

  const [lexical, setLexical] = useState<string>(definition.lexical || 'noun');

  const [wordsTranslation, setWordsTranslation] = useState<
    FormWordsTranslation[]
  >(
    initialiseWordsTranslation(
      definition.word,
      definition.aiDefinitions?.wordsTranslation
    )
  );
  const [definitions, setDefs] = useState<FormDefinition[]>(
    initialiseDefinitions(definition.aiDefinitions?.definitions)
  );
  const [examples, setExamples] = useState<FormExample[]>(
    initialiseExamples(definition.aiDefinitions?.examples) || []
  );

  const selectedLanguageRef = useRef<LanguageCodes | null>(null);

  const { showError } = useSnackbar();

  const {
    isVisible: isConfirmDeleteVisible,
    // openModal: openConfirmDeleteModal,
    closeModal: closeConfirmDeleteModal,
  } = useSimpleModalState();

  const {
    isVisible: isConfirmRemoveTranslationVisible,
    openModal: openConfirmRemoveTranslationModal,
    closeModal: closeConfirmRemoveTranslationModal,
  } = useSimpleModalState();

  const isManualAdd = !definition.id;

  const hasSpanishTranslations = hasTranslations({
    language: LANGUAGE_CODE_ES_US,
    definitions,
  });

  const hasSavedSpanishTranslations = hasSavedTranslations({
    language: LANGUAGE_CODE_ES_US,
    allDefinitions,
    currentDefinitionId: definition.id || 0,
  });

  const { loading: loadingRemoveFromComponent, handleRemoveFromComponent } =
    useRemoveDefinition({
      componentId,
      sectionId,
      definitions: allDefinitions,
      onCompleted: () => {
        closeConfirmDeleteModal();
        goBack();
      },
    });

  const { saveToDictionary, loadingSaveToDictionary, loadingUpdateComponent } =
    useSaveToDictionaryBook({
      shouldUpdateComponent: isManualAdd,
      componentId,
      sectionId,
      vocabularyIds: allDefinitions.map(d => d.id || 0),
      setWord,
      goTo,
    });

  const { loadingRemoveFromDictionary, removeFromDictionary } =
    useRemoveFromDictionaryBook();

  const [deleteFromDictionary, { loading: loadingDeleteFromDictionary }] =
    useLoggingMutation<
      DeleteFromDictionaryMutation,
      DeleteFromDictionaryMutationVariables
    >(DELETE_FROM_DICTIONARY, {
      onCompleted: () => {
        if (definition.id) handleRemoveFromComponent(definition.id);
      },
      onError: () =>
        showError('There was a problem deleting the word from the dictionary'),
    });

  const {
    translateTo,
    loading: loadingTranslation,
    error: translateToError,
  } = useTranslateTo();

  const handleAddTranslation = useCallback(
    (language: LanguageCodes) => {
      translateTo({
        variables: {
          input: {
            sentences: collectSentences(
              definitions,
              examples,
              wordsTranslation
            ),
            language,
          },
        },
      }).then(res => {
        const {
          definitions: updatedDefinitions,
          examples: updatedExamples,
          wordsTranslation: updatedWordsTranslation,
        } = assignTranslations({
          definitions,
          examples,
          translations: res.data?.translateTo.translations || [],
          language,
          wordsTranslation,
        });
        setWordsTranslation([...updatedWordsTranslation]);
        setDefs([...updatedDefinitions]);
        setExamples([...updatedExamples]);
      });
    },
    [definitions, examples, translateTo, wordsTranslation]
  );

  const isFormValid = useCallback(() => {
    const {
      hasError,
      wordsTranslation: updatedWordsTranslation,
      definitions: updatedDefinitions,
      examples: updatedExamples,
    } = validateForm(definitions, examples, wordsTranslation);

    if (updatedWordsTranslation) {
      setWordsTranslation([...updatedWordsTranslation]);
    }
    setDefs([...updatedDefinitions]);
    setExamples([...updatedExamples]);

    if (hasError) {
      setFormErrors(getFieldErrors(definitions, examples, wordsTranslation));
    }

    return !hasError;
  }, [definitions, examples, setFormErrors, wordsTranslation]);

  const onSubmit = useCallback(() => {
    saveToDictionary({
      definition,
      wordsTranslation,
      examples,
      definitions,
      lexical,
    });
  }, [
    definition,
    lexical,
    wordsTranslation,
    examples,
    definitions,
    saveToDictionary,
  ]);

  const handleRemoveTranslation = useCallback(
    (language: LanguageCodes | null) => {
      if (!language || language === LANGUAGE_CODE_EN_US) {
        return;
      }

      const updatedTranslations = wordsTranslation.filter(
        w => w.language !== language
      );
      const updatedExamples = examples.filter(e => e.language !== language);
      const updatedDefinitions = definitions.filter(
        d => d.language !== language
      );
      const removeLanguageTranslations = () => {
        setWordsTranslation(updatedTranslations);
        setDefs(updatedDefinitions);
        setExamples(updatedExamples);
        setCurrentLanguage?.(LANGUAGE_CODE_EN_US);
        closeConfirmRemoveTranslationModal();
      };

      if (hasSavedSpanishTranslations) {
        removeFromDictionary({
          definition,
          wordsTranslation: updatedTranslations,
          examples: updatedExamples,
          definitions: updatedDefinitions,
          isManualAdd,
          lexical,
        }).then(() => removeLanguageTranslations());
        // delay clearing the form and changing the language tab
        // until the after mutation is completed
      } else {
        // if no saved translations, clear the form immediately
        removeLanguageTranslations();
      }
    },
    [
      definition,
      definitions,
      examples,
      hasSavedSpanishTranslations,
      isManualAdd,
      lexical,
      removeFromDictionary,
      setCurrentLanguage,
      closeConfirmRemoveTranslationModal,
      wordsTranslation,
    ]
  );

  const confirmRemoveTranslation = useCallback(
    (language: LanguageCodes) => {
      selectedLanguageRef.current = language;
      openConfirmRemoveTranslationModal();
    },
    [openConfirmRemoveTranslationModal]
  );

  return (
    <ActivityBuilderDrawerForm>
      {isConfirmDeleteVisible && (
        <DeleteWordFromDictionaryConfirm
          word={definition.word}
          isLoading={loadingDeleteFromDictionary || loadingRemoveFromComponent}
          onClose={closeConfirmDeleteModal}
          onConfirm={() => {
            if (definition.id) {
              deleteFromDictionary({
                variables: {
                  input: {
                    dictionaryId: definition.id,
                  },
                },
              });
            }
          }}
        />
      )}
      {isConfirmRemoveTranslationVisible && selectedLanguageRef.current && (
        <RemoveTranslationFromDictionaryConfirm
          isLoading={loadingRemoveFromDictionary}
          language={selectedLanguageRef.current}
          word={definition.word}
          onConfirm={() =>
            handleRemoveTranslation?.(selectedLanguageRef.current)
          }
          onClose={closeConfirmRemoveTranslationModal}
        />
      )}
      <ActivityBuilderDrawerWrapper>
        <ActivityBuilderDrawerHeader
          isBack
          title={definition.word}
          onClose={goBack}

          // Hiding this option for now as it is creating bugs
          // for draft / published lessons that share the word

          // Either these bugs will be fixed in a future PR
          // or this option will be removed entirely
          // and the associated modals / mutations etc will also be removed

          // headerButton={
          //   !isManualAdd ? (
          //     <DropdownKebab
          //       $isSmallToggle
          //       aria-label="Definition options"
          //       dropdownContents={
          //         <>
          //           <DropdownItem $isNegative onClick={openConfirmDeleteModal}>
          //             Delete from Database
          //           </DropdownItem>
          //         </>
          //       }
          //     />
          //   ) : null
          // }
        />
        <ActivityBuilderDrawerContent>
          <VocabularySettingForm
            isSavedAndApproved={!isManualAdd}
            isLoading={false}
            isLoadingLanguage={loadingTranslation}
            hasTranslateError={!!translateToError}
            isManualAdd={isManualAdd}
            lexicalCategory={lexical}
            shouldGenerateImages={false}
            wordsTranslation={wordsTranslation}
            definitions={definitions}
            examples={examples}
            word={definition.word}
            updateDefinitions={setDefs}
            updateExamples={setExamples}
            updateLexical={isManualAdd ? value => setLexical(value) : undefined}
            handleAddTranslation={handleAddTranslation}
            updateWordsTranslation={setWordsTranslation}
            handleRemoveLanguage={confirmRemoveTranslation}
          />
        </ActivityBuilderDrawerContent>
        <ActivityBuilderDrawerActions>
          <PrimaryButton
            type="button"
            loading={loadingSaveToDictionary || loadingUpdateComponent}
            disabled={
              loadingSaveToDictionary ||
              loadingUpdateComponent ||
              loadingTranslation ||
              loadingRemoveFromDictionary
            }
            onClick={() => {
              if (isFormValid()) {
                onSubmit();
              }
            }}
            data-selector={isManualAdd ? 'btn-save-and-approve' : 'btn-save'}
          >
            {isManualAdd ? 'Save and Approve' : 'Save'}
          </PrimaryButton>
          {hasSpanishTranslations &&
            currentLanguage === LANGUAGE_CODE_ES_US && (
              <NegativeButton
                type="button"
                variant="outlined"
                $isDisabled={
                  loadingSaveToDictionary || loadingRemoveFromDictionary
                }
                onClick={() => confirmRemoveTranslation(LANGUAGE_CODE_ES_US)}
                data-selector="btn-remove-spanish"
              >
                Remove Spanish
              </NegativeButton>
            )}
        </ActivityBuilderDrawerActions>
      </ActivityBuilderDrawerWrapper>
    </ActivityBuilderDrawerForm>
  );
};

export default VocabularySettingEditDefinition;
